<template>
  <div class="container-fluid py-4">

    <!-- En-tête -->
    <div class="d-flex justify-content-between align-items-center mb-4 header-clinique flex-wrap gap-2">
      <div class="d-flex align-items-center gap-3">
        <i class="bi bi-buildings fs-2 text-primary"></i>
        <div>
          <h2 class="fw-bold mb-0">Cliniques clientes</h2>
          <small class="text-muted">{{ principal ? 'Abonnements, accès et ouverture de nouvelles cliniques.' : "Ouverture des cliniques en essai, coordonnées et activité. L'abonnement est réglé par l'administrateur principal." }}</small>
        </div>
      </div>
      <button class="btn btn-primary btn-clinique" @click="ouvrirCreation">
        <i class="bi bi-plus-circle me-1"></i> Nouvelle clinique
      </button>
    </div>

    <!-- Indicateurs -->
    <div class="row g-3 mb-4">
      <div class="col-6 col-md" v-for="k in indicateurs" :key="k.libelle">
        <div class="card card-clinique p-3 h-100">
          <small class="text-muted">{{ k.libelle }}</small>
          <div class="fs-3 fw-bold" :class="k.classe">{{ k.valeur }}</div>
        </div>
      </div>
    </div>

    <div class="card card-clinique p-3 mb-3">
      <div class="row g-2 align-items-end">
        <div class="col-12 col-md-5">
          <label class="form-label small fw-semibold mb-1">Rechercher</label>
          <input v-model="recherche" type="search" class="form-control" placeholder="Nom, ville, domaine, contact…">
        </div>
        <div class="col-6 col-md-3">
          <label class="form-label small fw-semibold mb-1">Statut</label>
          <select v-model="statut" class="form-select">
            <option value="">Toutes</option>
            <option value="ACTIF">Abonnées</option>
            <option value="ESSAI">En essai</option>
            <option value="SUSPENDU">Suspendues</option>
            <option value="ARELANCER">À relancer (≤ 7 j)</option>
            <option value="SUPPRIMEE">Supprimées{{ compteurs.supprimees ? ` (${compteurs.supprimees})` : '' }}</option>
          </select>
        </div>
        <div class="col-6 col-md-3">
          <label class="form-label small fw-semibold mb-1">Trier par</label>
          <select v-model="tri" class="form-select">
            <option value="c.nom">Nom</option>
            <option value="c.dateFinAbonnement">Fin d'abonnement</option>
            <option value="c.createdAt">Date d'ouverture</option>
          </select>
        </div>
      </div>
    </div>

    <div v-if="erreur" class="alert alert-danger d-flex justify-content-between align-items-center">
      <span>{{ erreur }}</span>
      <button class="btn btn-sm btn-outline-danger" @click="charger">Réessayer</button>
    </div>

    <div class="card shadow-sm card-clinique">
      <div class="table-responsive" :class="{ 'opacity-50': chargement }">
        <table class="table align-middle mb-0 table-hover modern-table-clinique">
          <thead>
            <tr>
              <th>Clinique</th>
              <th>Offre</th>
              <th>Statut</th>
              <th>Activité</th>
              <th>Fin d'abonnement</th>
              <th>Utilisateurs max</th>
              <th>Contact</th>
              <th class="text-end">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="c in lignes" :key="c.id">
              <td>
                <a href="#" class="fw-semibold text-decoration-none" title="Voir la fiche de la clinique" @click.prevent="fiche = c">{{ c.nom }}</a>
                <small class="text-muted">{{ c.domaine || c.slug }}<span v-if="c.ville"> · {{ c.ville }}</span><span v-if="c.pays"> · {{ c.pays }}</span></small>
                <div v-if="c.apporteurInfo" class="small text-primary" :title="`Apporteur depuis le ${formatDate(c.apporteurInfo.depuis)}`"><i class="bi bi-person-badge me-1"></i>{{ c.apporteurInfo.nom }} · {{ c.apporteurInfo.taux }} %</div>
              </td>
              <td>
                <div>{{ c.planInfo ? c.planInfo.nom : 'Offre à choisir' }}</div>
                <small class="text-muted" v-if="c.planInfo">{{ c.periodicite === 'ANNUEL' ? 'annuel' : 'mensuel' }}</small>
              </td>
              <td>
                <span class="badge" :class="badgeStatut(c)">{{ libelleStatut(c) }}</span>
                <div v-if="c.changementPrevu" class="small mt-1 text-muted" title="Offre moins chère déjà payée : elle s'applique à cette date">
                  <i class="bi bi-arrow-down-right me-1"></i>{{ c.changementPrevu.plan.nom }} le {{ dateCourte(c.changementPrevu.le) }}
                  <button v-if="principal" class="btn btn-link btn-sm p-0 ms-1 align-baseline" title="Annuler ce changement : la clinique garde son offre actuelle" @click="annulerChangementPrevu(c)">annuler</button>
                </div>
                <div v-if="c.resiliation" class="small mt-1" :title="c.resiliation.motif ? 'Motif : ' + c.resiliation.motif : 'Aucun motif donné'">
                  <span v-if="c.resiliation.aSupprimer" class="text-danger"><i class="bi bi-trash me-1"></i>Partie : données conservées jusqu'au {{ dateCourte(c.resiliation.conservationJusquau) }}, à supprimer</span>
                  <span v-else class="text-danger"><i class="bi bi-box-arrow-right me-1"></i>Ne renouvelle pas (fin le {{ dateCourte(c.resiliation.echeance) }})</span>
                </div>
                <div v-if="c.donneesEssai" class="small mt-1" :title="infoDonneesEssai(c)">
                  <span v-if="c.donneesEssai.choix === 'GARDEES'" class="text-muted"><i class="bi bi-check2-circle me-1"></i>Essai conservé</span>
                  <span v-else-if="c.donneesEssai.choix === 'EFFACEES'" class="text-muted"><i class="bi bi-eraser me-1"></i>Essai effacé</span>
                  <span v-else-if="c.donneesEssai.aChoisir" class="text-warning-emphasis"><i class="bi bi-hourglass-split me-1"></i>Données d'essai : choix en attente</span>
                  <span v-else class="text-muted"><i class="bi bi-check2-circle me-1"></i>Essai conservé (par défaut)</span>
                </div>
              </td>
              <td role="button" title="Voir les utilisateurs et les connexions" @click="voirActivite(c)">
                <div class="d-flex align-items-center gap-1 text-nowrap">
                  <span class="point-activite" :class="classeActivite(c)"></span>
                  <span class="small fw-semibold">{{ c.activite && c.activite.derniereActivite ? depuis(c.activite.derniereActivite) : 'Jamais connectée' }}</span>
                </div>
                <div v-if="c.activite && c.activite.utilisateurs" class="small text-muted">
                  {{ c.activite.actifs7j }}/{{ c.activite.utilisateurs }} actif(s) sur 7 j<span v-if="c.activite.parQui"> · {{ c.activite.parQui }}</span>
                </div>
              </td>
              <td>
                <span v-if="c.dateFinAbonnement">{{ formatDate(c.dateFinAbonnement) }}</span>
                <span v-else class="text-muted">Sans échéance</span>
                <div v-if="c.joursRestants !== null" class="small" :class="c.joursRestants < 0 ? 'text-danger' : (c.joursRestants <= 7 ? 'text-warning' : 'text-muted')">
                  {{ c.joursRestants < 0 ? `échu depuis ${-c.joursRestants} j` : `${c.joursRestants} j restants` }}
                </div>
              </td>
              <td>{{ c.maxUtilisateurs ?? 'Illimité' }}</td>
              <td class="small">
                <div>{{ c.contactNom }}</div>
                <div class="text-muted">{{ c.contactTelephone }}</div>
              </td>
              <td class="text-end text-nowrap">
                <button v-if="c.statut === 'SUPPRIMEE' && principal" class="btn btn-sm btn-outline-secondary" title="Sauvegardes et restauration" @click="donnees = c"><i class="bi bi-database me-1"></i>Données</button>
                <div v-else-if="!principal && c.statut !== 'SUPPRIMEE'" class="btn-group btn-group-sm">
                  <button class="btn btn-outline-secondary" title="Fiche de la clinique (imprimable)" @click="fiche = c"><i class="bi bi-card-text"></i></button>
                  <button class="btn btn-outline-secondary" title="Données : volume et sauvegarde" @click="donnees = c"><i class="bi bi-database"></i></button>
                  <router-link class="btn btn-outline-secondary" title="Factures de la clinique (émettre, encaisser)" :to="{ name: 'plateforme_factures', query: { clinique: c.id } }"><i class="bi bi-receipt"></i></router-link>
                  <button class="btn btn-outline-primary" title="Modifier les coordonnées" @click="ouvrirEdition(c)"><i class="bi bi-pencil"></i></button>
                </div>
                <div v-else-if="principal" class="btn-group btn-group-sm">
                  <button class="btn btn-outline-secondary" title="Fiche de la clinique (imprimable)" @click="fiche = c"><i class="bi bi-card-text"></i></button>
                  <button class="btn btn-outline-secondary" title="Données : sauvegardes, export, réinitialisation, suppression" @click="donnees = c"><i class="bi bi-database"></i></button>
                  <router-link class="btn btn-outline-secondary" title="Factures de la clinique" :to="{ name: 'plateforme_factures', query: { clinique: c.id } }"><i class="bi bi-receipt"></i></router-link>
                  <button class="btn btn-outline-success" title="Offrir une période gratuite (tracée, annulable)" @click="ouvrirOffre(c)"><i class="bi bi-gift me-1"></i>Offrir</button>
                  <button class="btn btn-outline-primary" title="Modifier" @click="ouvrirEdition(c)"><i class="bi bi-pencil"></i></button>
                  <button
                    class="btn"
                    :class="c.statut === 'SUSPENDU' ? 'btn-outline-success' : 'btn-outline-danger'"
                    @click="basculerSuspension(c)"
                  >
                    {{ c.statut === 'SUSPENDU' ? 'Réactiver' : 'Suspendre' }}
                  </button>
                </div>
              </td>
            </tr>
            <tr v-if="!chargement && lignes.length === 0">
              <td colspan="8" class="text-center text-muted py-4">{{ recherche || statut ? 'Aucune clinique ne correspond à la recherche.' : 'Aucune clinique pour le moment.' }}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="px-3 pb-3">
        <Pagination v-model:page="page" v-model:par-page="parPage" :total="total" />
      </div>
    </div>

    <!-- Fenêtre création / modification -->
    <div v-if="formulaire" class="modal-backdrop-custom" @click.self="formulaire = null">
      <div class="modal-card card shadow">
        <div class="card-header d-flex justify-content-between align-items-center">
          <h5 class="mb-0">{{ formulaire.id ? 'Modifier la clinique' : 'Nouvelle clinique' }}</h5>
          <button class="btn-close" @click="formulaire = null"></button>
        </div>
        <div class="card-body">
          <div class="row g-3">
            <div class="col-md-6">
              <label class="form-label">Nom de la clinique *</label>
              <input v-model="formulaire.nom" class="form-control" :disabled="!!formulaire.id">
            </div>
            <div class="col-md-6">
              <label class="form-label">Ville</label>
              <input v-model="formulaire.ville" class="form-control">
            </div>
            <div class="col-md-6">
              <label class="form-label">Pays</label>
              <input v-model="formulaire.pays" class="form-control">
            </div>
            <div class="col-md-6">
              <label class="form-label">Indicatif téléphonique</label>
              <input v-model.trim="formulaire.indicatifTelephone" class="form-control" placeholder="+…" maxlength="6">
              <small class="text-muted">Ajouté aux numéros saisis sans indicatif (SMS aux patients).</small>
            </div>
            <div v-if="principal" class="col-md-6">
              <label class="form-label">Adresse du site (domaine)</label>
              <input v-model="formulaire.domaine" class="form-control" placeholder="ex. clinique.votredomaine.com">
              <small class="text-muted">Affiche le nom de la clinique sur l'écran de connexion.</small>
            </div>
            <div v-if="!principal" class="col-12">
              <div class="alert alert-light border small py-2 mb-0">
                <i class="bi bi-info-circle me-1"></i>
                <template v-if="formulaire.id">Offre : <strong>{{ formulaire.planInfo ? formulaire.planInfo.nom : '—' }}</strong> · statut : {{ libelleStatut(formulaire) }}. L'abonnement, le statut et les accès sont réglés par l'administrateur principal.</template>
                <template v-else>La clinique s'ouvre en <strong>essai gratuit</strong> sur l'offre choisie. L'administrateur principal règle ensuite l'abonnement.</template>
              </div>
            </div>
            <div v-if="principal || !formulaire.id" class="col-md-3">
              <label class="form-label">Offre</label>
              <select v-model="formulaire.plan" class="form-select" required>
                <option :value="null" disabled>Choisir une offre</option>
                <option v-for="p in plans.filter(x => x.actif || x.id === formulaire.plan)" :key="p.id" :value="p.id">{{ p.nom }} — {{ fcfa(p.prixMensuelRemise ?? p.prixMensuel) }}/mois{{ p.partenaire ? ' (partenaire)' : '' }}</option>
              </select>
            </div>
            <div v-if="principal" class="col-md-3">
              <label class="form-label">Statut</label>
              <select v-model="formulaire.statut" class="form-select">
                <option value="ESSAI">Essai</option>
                <option value="ACTIF">Actif</option>
                <option value="SUSPENDU">Suspendu</option>
              </select>
            </div>
            <div v-if="principal" class="col-md-4">
              <label class="form-label">Fin d'abonnement</label>
              <input v-model="formulaire.dateFinAbonnement" type="date" class="form-control">
              <small class="text-muted" v-if="!formulaire.id">Vide = 30 jours d'essai.</small>
            </div>
            <div v-if="principal" class="col-md-4">
              <label class="form-label">Utilisateurs max</label>
              <input v-model.number="formulaire.maxUtilisateurs" type="number" min="1" class="form-control" :placeholder="placeholderMax">
              <small class="text-muted">Vide = limite de l'offre.</small>
            </div>
            <div class="col-12" v-if="formulaire.plan && principal">
              <label class="form-label mb-1">Modules ajoutés à la carte</label>
              <div class="d-flex flex-wrap gap-3">
                <div v-for="(m, code) in modulesHorsOffre" :key="code" class="form-check">
                  <input class="form-check-input" type="checkbox" :id="'mod-' + code" :value="code" v-model="formulaire.modulesSupplementaires">
                  <label class="form-check-label" :for="'mod-' + code">{{ m.nom }}</label>
                </div>
                <span v-if="Object.keys(modulesHorsOffre).length === 0" class="text-muted small">L'offre comprend déjà tous les modules.</span>
              </div>
            </div>
            <div class="col-md-4">
              <label class="form-label">Contact (nom)</label>
              <input v-model="formulaire.contactNom" class="form-control">
            </div>
            <div class="col-md-6">
              <label class="form-label">Téléphone du contact</label>
              <input v-model="formulaire.contactTelephone" class="form-control">
            </div>
            <div class="col-md-6">
              <label class="form-label">Email du contact</label>
              <input v-model="formulaire.contactEmail" type="email" class="form-control">
            </div>

            <template v-if="!formulaire.id">
              <div class="col-12"><hr class="my-1"><h6 class="mb-0">Administrateur de la clinique</h6>
                <small class="text-muted">Il recevra un lien par email pour choisir son mot de passe.</small>
              </div>
              <div class="col-md-4">
                <label class="form-label">Prénom *</label>
                <input v-model="formulaire.admin.prenom" class="form-control">
              </div>
              <div class="col-md-4">
                <label class="form-label">Nom *</label>
                <input v-model="formulaire.admin.nom" class="form-control">
              </div>
              <div class="col-md-4">
                <label class="form-label">Email *</label>
                <input v-model="formulaire.admin.email" type="email" class="form-control">
              </div>
            </template>

            <template v-if="principal">
            <div class="col-12"><hr class="my-1"><h6 class="mb-0"><i class="bi bi-person-badge me-1"></i>Apporteur d'affaires</h6>
              <small class="text-muted">Celui qui a amené la clinique : il touche une commission sur ses abonnements payés.
                <router-link :to="{ name: 'plateforme_apporteurs' }">Gérer les apporteurs</router-link></small>
            </div>
            <div class="col-md-5">
              <label class="form-label">Apporteur</label>
              <select v-model="formulaire.apporteur" class="form-select" @change="formulaire.apporteurDepuis = formulaire.apporteur ? aujourdhui() : ''">
                <option :value="null">Aucun</option>
                <option v-for="a in apporteurs.filter(x => x.actif || x.id === formulaire.apporteur)" :key="a.id" :value="a.id">{{ a.nom }} — {{ a.tauxCommission }} %</option>
              </select>
            </div>
            <div class="col-md-3" v-if="formulaire.apporteur">
              <label class="form-label">Taux propre (%)</label>
              <input v-model.number="formulaire.commissionTaux" type="number" min="0" max="100" class="form-control" :placeholder="`${apporteurChoisi ? apporteurChoisi.tauxCommission : ''} (apporteur)`">
              <small class="text-muted">Vide = taux de l'apporteur.</small>
            </div>
            <div class="col-md-4" v-if="formulaire.apporteur && formulaire.id">
              <label class="form-label">Commissions à partir du</label>
              <input v-model="formulaire.apporteurDepuis" type="date" class="form-control">
              <small class="text-muted">Les factures déjà payées depuis cette date sont prises en compte.</small>
            </div>
            </template>

            <div class="col-12">
              <label class="form-label">Notes internes</label>
              <textarea v-model="formulaire.notes" rows="2" class="form-control"></textarea>
            </div>
          </div>

          <div v-if="erreurForm" class="alert alert-danger mt-3 mb-0 py-2">{{ erreurForm }}</div>
        </div>
        <div class="card-footer text-end">
          <button class="btn btn-secondary me-2" @click="formulaire = null">Annuler</button>
          <button class="btn btn-primary" :disabled="enregistrement" @click="enregistrer">
            {{ enregistrement ? 'Enregistrement…' : (formulaire.id ? 'Enregistrer' : 'Créer la clinique') }}
          </button>
        </div>
      </div>
    </div>

    <!-- Période offerte -->
    <div v-if="offre" class="modal-backdrop-custom" @click.self="offre = null">
      <div class="modal-card card shadow" style="max-width: 560px">
        <div class="card-header d-flex justify-content-between align-items-center">
          <h5 class="mb-0"><i class="bi bi-gift me-1"></i>Offrir une période · {{ offre.clinique.nom }}</h5>
          <button class="btn-close" @click="offre = null"></button>
        </div>
        <div class="card-body">
          <div class="row g-2 align-items-end">
            <div class="col-5">
              <label class="form-label">Durée</label>
              <input v-model.number="offre.nombre" type="number" min="1" :max="offre.unite === 'MOIS' ? 24 : 365" class="form-control">
            </div>
            <div class="col-7">
              <div class="btn-group w-100">
                <button class="btn" :class="offre.unite === 'JOURS' ? 'btn-primary' : 'btn-outline-primary'" @click="offre.unite = 'JOURS'">Jours</button>
                <button class="btn" :class="offre.unite === 'MOIS' ? 'btn-primary' : 'btn-outline-primary'" @click="offre.unite = 'MOIS'">Mois</button>
              </div>
            </div>
            <div class="col-12">
              <label class="form-label">Motif *</label>
              <input v-model.trim="offre.motif" class="form-control" placeholder="Essai prolongé, clinique pilote, compensation d'une panne…">
            </div>
          </div>
          <div class="alert alert-light border small mt-3 mb-0">
            Fin d'abonnement : <strong>{{ offre.clinique.dateFinAbonnement ? formatDate(offre.clinique.dateFinAbonnement) : 'sans échéance' }}</strong>
            → <strong class="text-success">{{ nouvelleFinOffre }}</strong>.
            Le statut ne change pas{{ offre.clinique.statut === 'ESSAI' ? ' (la clinique reste en essai)' : '' }}.
            Une facture « offerte » à 0 garde la trace ; en cas d'erreur, annulez-la depuis « Factures » : la même durée sera retirée.
          </div>
          <div v-if="erreurOffre" class="alert alert-danger mt-3 mb-0 py-2">{{ erreurOffre }}</div>
        </div>
        <div class="card-footer text-end">
          <button class="btn btn-secondary me-2" @click="offre = null">Annuler</button>
          <button class="btn btn-success" :disabled="enregistrement || !offre.nombre || offre.motif.length < 3" @click="offrir">Offrir</button>
        </div>
      </div>
    </div>

    <!-- Activité d'une clinique -->
    <div v-if="activite" class="modal-backdrop-custom" @click.self="activite = null">
      <div class="modal-card card shadow" style="max-width: 760px">
        <div class="card-header d-flex justify-content-between align-items-center">
          <h5 class="mb-0"><i class="bi bi-activity me-1"></i>Activité · {{ activite.clinique.nom }}</h5>
          <button class="btn-close" @click="activite = null"></button>
        </div>
        <div class="card-body">
          <div v-if="activite.chargement" class="text-center my-4"><div class="spinner-border text-primary"></div></div>
          <template v-else>
            <h6 class="fw-semibold">Utilisateurs</h6>
            <div class="table-responsive mb-3">
              <table class="table table-sm align-middle mb-0">
                <thead><tr><th>Nom</th><th>Rôle</th><th>Dernière connexion</th><th>Dernière action</th></tr></thead>
                <tbody>
                  <tr v-for="(u, i) in activite.utilisateurs" :key="i" :class="{ 'text-muted': !u.actif }">
                    <td>{{ u.nom || u.email }}<div class="small text-muted">{{ u.email }}<span v-if="!u.actif"> · désactivé</span></div></td>
                    <td class="small">{{ u.role || '—' }}</td>
                    <td class="small">{{ u.derniereConnexion ? dateHeure(u.derniereConnexion) : 'jamais' }}</td>
                    <td class="small"><span class="point-activite me-1" :class="classeDate(u.derniereActivite)"></span>{{ u.derniereActivite ? depuis(u.derniereActivite) : 'jamais' }}</td>
                  </tr>
                  <tr v-if="!activite.utilisateurs.length"><td colspan="4" class="text-muted text-center">Aucun utilisateur.</td></tr>
                </tbody>
              </table>
            </div>
            <h6 class="fw-semibold">Dernières connexions</h6>
            <ul v-if="activite.connexions.length" class="list-unstyled small mb-0">
              <li v-for="(k, i) in activite.connexions" :key="i">{{ dateHeure(k.date) }} — {{ k.nom || '?' }}<span v-if="k.ip" class="text-muted"> ({{ k.ip }})</span></li>
            </ul>
            <div v-else class="small text-muted">Aucune connexion enregistrée.</div>
          </template>
        </div>
      </div>
    </div>

    <DonneesClinique v-if="donnees" :clinique="donnees" @fermer="donnees = null" @modifiee="charger" />
    <FicheClinique v-if="fiche" :clinique="fiche" :catalogue-modules="modules" @fermer="fiche = null" />
  </div>
</template>

<script>
import api, { messageErreur, notifier } from '@/api'
import dayjs from 'dayjs'
import Pagination from '@/components/commun/Pagination.vue'
import { listeServeur } from '@/components/commun/listeServeur'
import { confirmer, demanderMotif } from '@/components/commun/dialogue'
import DonneesClinique from '@/components/plateforme/DonneesClinique.vue'
import FicheClinique from '@/components/plateforme/FicheClinique.vue'

const vide = () => ({
  nom: '', ville: '', pays: '', indicatifTelephone: '', domaine: '', offre: 'CLINIQUE', statut: 'ESSAI', dateFinAbonnement: '',
  maxUtilisateurs: null, contactNom: '', contactTelephone: '', contactEmail: '', notes: '',
  plan: null, modulesSupplementaires: [],
  apporteur: null, commissionTaux: null, apporteurDepuis: '',
  admin: { prenom: '', nom: '', email: '' }
})

export default {
  name: 'PlateformeCliniques',
  components: { Pagination, DonneesClinique, FicheClinique },
  mixins: [listeServeur('plateforme/cliniques', { tri: 'c.nom', sens: 'asc' })],
  data() {
    return {
      plans: [], modules: {}, apporteurs: [], formulaire: null, erreurForm: '', enregistrement: false, statut: '', donnees: null, fiche: null,
      offre: null, erreurOffre: '', activite: null,
      compteurs: { total: 0, actives: 0, essai: 0, aRelancer: 0, supprimees: 0, activesAujourdhui: 0 }
    }
  },
  watch: {
    statut() { this.rechargerListe() },
  },
  computed: {
    /** Administrateur principal (le gestionnaire ne gère que l'ouverture et les coordonnées). */
    principal() {
      return !this.$store.getters.isPlatformGestionnaire
    },
    planChoisi() {
      return this.plans.find(p => p.id === this.formulaire?.plan) || null
    },
    apporteurChoisi() {
      return this.apporteurs.find(a => a.id === this.formulaire?.apporteur) || null
    },
    modulesHorsOffre() {
      const inclus = this.planChoisi ? this.planChoisi.modules : []
      return Object.fromEntries(Object.entries(this.modules).filter(([code]) => !inclus.includes(code)))
    },
    nouvelleFinOffre() {
      const o = this.offre
      if (!o || !o.nombre) return '—'
      const fin = o.clinique.dateFinAbonnement ? dayjs(o.clinique.dateFinAbonnement) : null
      const depart = fin && fin.isAfter(dayjs(), 'day') ? fin : dayjs().startOf('day')
      return depart.add(o.nombre, o.unite === 'MOIS' ? 'month' : 'day').format('DD/MM/YYYY')
    },
    placeholderMax() {
      if (!this.planChoisi) return 'Illimité'
      return this.planChoisi.maxUtilisateurs ? `${this.planChoisi.maxUtilisateurs} (offre)` : 'Illimité (offre)'
    },
    indicateurs() {
      const { total, actives, essai, aRelancer } = this.compteurs
      return [
        { libelle: 'Cliniques', valeur: total, classe: '' },
        { libelle: 'Abonnées', valeur: actives, classe: 'text-success' },
        { libelle: 'En essai', valeur: essai, classe: 'text-primary' },
        { libelle: 'À relancer (≤ 7 j)', valeur: aRelancer, classe: aRelancer ? 'text-danger' : '' },
        { libelle: "Actives aujourd'hui", valeur: this.compteurs.activesAujourdhui || 0, classe: 'text-success' }
      ]
    }
  },
  created() {
    this.chargerPlans()
    if (this.principal) this.chargerApporteurs()
  },
  methods: {
    filtresListe() {
      return { statut: this.statut }
    },
    apresChargement(lignes, corps) {
      if (corps.indicateurs) this.compteurs = corps.indicateurs
      return lignes
    },
    async chargerPlans() {
      try {
        const plans = await api.getItems('plateforme/plans')
        this.plans = plans.data.data
        this.modules = plans.data.modules
      } catch (e) {
        notifier(messageErreur(e, 'Impossible de charger les offres.'), 'danger')
      }
    },
    /** Temps écoulé, en clair : « à l'instant », « il y a 3 h », « il y a 5 j ». */
    depuis(d) {
      const min = dayjs().diff(dayjs(d), 'minute')
      if (min < 2) return "à l'instant"
      if (min < 60) return `il y a ${min} min`
      const h = Math.floor(min / 60)
      if (h < 24) return `il y a ${h} h`
      const j = Math.floor(h / 24)
      return j < 60 ? `il y a ${j} j` : `le ${dayjs(d).format('DD/MM/YYYY')}`
    },
    dateHeure(d) {
      return d ? dayjs(d).format('DD/MM/YYYY HH:mm') : ''
    },
    /** Vert : aujourd'hui · orange : cette semaine · rouge : plus d'une semaine · gris : jamais */
    classeDate(d) {
      if (!d) return 'gris'
      const h = dayjs().diff(dayjs(d), 'hour')
      return h < 24 ? 'vert' : (h < 24 * 7 ? 'orange' : 'rouge')
    },
    classeActivite(c) {
      return this.classeDate(c.activite?.derniereActivite)
    },
    async voirActivite(c) {
      this.activite = { clinique: c, chargement: true, utilisateurs: [], connexions: [] }
      try {
        const r = (await api.custom(`plateforme/cliniques/${c.id}/activite`, 'GET')).data
        this.activite = { clinique: c, chargement: false, utilisateurs: r.utilisateurs, connexions: r.connexions }
      } catch (e) {
        this.activite = null
        notifier(messageErreur(e), 'danger')
      }
    },
    aujourdhui() {
      return dayjs().format('YYYY-MM-DD')
    },
    async chargerApporteurs() {
      try {
        this.apporteurs = (await api.custom('plateforme/apporteurs', 'GET')).data.data
      } catch (e) {
        this.apporteurs = []
      }
    },
    ouvrirCreation() {
      this.erreurForm = ''
      this.formulaire = vide()
      this.formulaire.plan = this.plans[0]?.id || null
    },
    ouvrirEdition(c) {
      this.erreurForm = ''
      this.formulaire = {
        ...vide(), ...c,
        plan: c.planInfo ? c.planInfo.id : null,
        // Seuls les modules réellement ajoutés à la carte (pas ceux de l'offre)
        modulesSupplementaires: [...(c.modulesSupplementaires || [])],
        dateFinAbonnement: c.dateFinAbonnement ? c.dateFinAbonnement.slice(0, 10) : '',
        apporteur: c.apporteurInfo ? c.apporteurInfo.id : null,
        commissionTaux: c.apporteurInfo ? c.apporteurInfo.tauxPropre : null,
        apporteurDepuis: c.apporteurInfo?.depuis || '',
        apporteurAvant: c.apporteurInfo ? { id: c.apporteurInfo.id, taux: c.apporteurInfo.tauxPropre, depuis: c.apporteurInfo.depuis || '' } : null
      }
      if (!c.planInfo) this.formulaire.modulesSupplementaires = []
    },
    champsModifiables(f) {
      // Gestionnaire : seulement les coordonnées (le serveur refuse le reste)
      if (!this.principal) {
        return {
          contactNom: f.contactNom || null,
          contactTelephone: f.contactTelephone || null,
          contactEmail: f.contactEmail || null,
          ville: f.ville || null,
          pays: f.pays || null,
          indicatifTelephone: f.indicatifTelephone || null,
          notes: f.notes || null
        }
      }
      return {
        domaine: f.domaine || null,
        statut: f.statut,
        dateFinAbonnement: f.dateFinAbonnement || null,
        maxUtilisateurs: f.maxUtilisateurs || null,
        contactNom: f.contactNom || null,
        contactTelephone: f.contactTelephone || null,
        contactEmail: f.contactEmail || null,
        ville: f.ville || null,
        pays: f.pays || null,
        indicatifTelephone: f.indicatifTelephone || null,
        notes: f.notes || null
      }
    },
    async enregistrer() {
      this.erreurForm = ''
      this.enregistrement = true
      try {
        if (this.formulaire.id) {
          await api.patchMergeItem('plateforme/cliniques', this.formulaire.id, this.champsModifiables(this.formulaire))
          if (!this.principal) {
            notifier('Coordonnées mises à jour.', 'success')
            this.formulaire = null
            await this.charger()
            return
          }
          await api.custom(`plateforme/cliniques/${this.formulaire.id}/offre`, 'PUT', {
            plan: this.formulaire.plan,
            modulesSupplementaires: this.formulaire.plan ? this.formulaire.modulesSupplementaires : [],
            maxUtilisateurs: this.formulaire.maxUtilisateurs || null
          })
          const f = this.formulaire
          const avant = f.apporteurAvant
          const taux = f.commissionTaux === '' || f.commissionTaux === null || f.commissionTaux === undefined ? null : f.commissionTaux
          if ((avant?.id || null) !== (f.apporteur || null) || (f.apporteur && ((avant?.taux ?? null) !== taux || (avant?.depuis || '') !== (f.apporteurDepuis || '')))) {
            const r = await api.custom(`plateforme/cliniques/${f.id}/apporteur`, 'PUT', {
              apporteur: f.apporteur || null,
              commissionTaux: taux,
              depuis: f.apporteur && (avant?.id !== f.apporteur || (avant?.depuis || '') !== (f.apporteurDepuis || '')) ? (f.apporteurDepuis || null) : null
            })
            if (r.data?.message) notifier(r.data.message, 'success')
          }
          notifier('Clinique mise à jour.', 'success')
        } else {
          const res = await api.createItem('plateforme/cliniques', {
            nom: this.formulaire.nom, ...this.champsModifiables(this.formulaire),
            plan: this.formulaire.plan,
            apporteur: this.formulaire.apporteur || null,
            commissionTaux: this.formulaire.apporteur && this.formulaire.commissionTaux !== '' ? this.formulaire.commissionTaux : null,
            offre: ['ESSENTIEL', 'CLINIQUE', 'GROUPE'].includes(this.planChoisi?.code) ? this.planChoisi.code : 'CLINIQUE',
            admin: this.formulaire.admin
          })
          if (this.principal && res.data?.id && this.formulaire.modulesSupplementaires.length) {
            await api.custom(`plateforme/cliniques/${res.data.id}/offre`, 'PUT', { modulesSupplementaires: this.formulaire.modulesSupplementaires })
          }
          notifier(res.data?.message || 'Clinique créée.', 'success')
        }
        this.formulaire = null
        await this.charger()
      } catch (e) {
        this.erreurForm = messageErreur(e, "L'enregistrement a échoué.")
      } finally {
        this.enregistrement = false
      }
    },
    ouvrirOffre(c) {
      this.erreurOffre = ''
      this.offre = { clinique: c, nombre: 1, unite: 'MOIS', motif: '' }
    },
    async offrir() {
      this.enregistrement = true
      this.erreurOffre = ''
      try {
        const o = this.offre
        const r = (await api.post(`plateforme/cliniques/${o.clinique.id}/periode-offerte`, { nombre: o.nombre, unite: o.unite, motif: o.motif })).data
        notifier(`${r.facture.libellePeriode} pour « ${o.clinique.nom} » : abonnement jusqu'au ${this.formatDate(r.clinique.dateFinAbonnement)}.`, 'success')
        this.offre = null
        await this.charger()
      } catch (e) {
        this.erreurOffre = messageErreur(e, "L'offre n'a pas pu être enregistrée.")
      } finally {
        this.enregistrement = false
      }
    },
    async basculerSuspension(c) {
      const suspendre = c.statut !== 'SUSPENDU'
      const ok = await confirmer({
        titre: suspendre ? 'Suspendre la clinique' : 'Réactiver la clinique',
        message: suspendre ? `Suspendre « ${c.nom} » ? Ses utilisateurs ne pourront plus se connecter.` : `Réactiver « ${c.nom} » ?`,
        libelleValider: suspendre ? 'Suspendre' : 'Réactiver',
        danger: suspendre,
      })
      if (!ok) return
      try {
        await api.patchMergeItem('plateforme/cliniques', c.id, { statut: suspendre ? 'SUSPENDU' : 'ACTIF' })
        notifier(suspendre ? 'Clinique suspendue.' : 'Clinique réactivée.', 'success')
        await this.charger()
      } catch (e) {
        notifier(messageErreur(e, 'Le changement de statut a échoué.'), 'danger')
      }
    },
    fcfa(v) {
      return new Intl.NumberFormat('fr-FR').format(v || 0) + ' FCFA'
    },
    libelleStatut(c) {
      if (c.statut === 'SUPPRIMEE') return 'Supprimée'
      if (c.statut === 'SUSPENDU') return 'Suspendue'
      if (c.joursRestants !== null && c.joursRestants < -7) return 'Expirée'
      return { ESSAI: 'Essai', ACTIF: 'Active' }[c.statut] || c.statut
    },
    async annulerChangementPrevu(c) {
      const r = await demanderMotif({
        titre: `Annuler le passage à ${c.changementPrevu.plan.nom}`,
        message: `${c.nom} gardera son offre actuelle au lieu de passer à ${c.changementPrevu.plan.nom} le ${this.dateCourte(c.changementPrevu.le)}. La période suivante a été payée au tarif ${c.changementPrevu.plan.nom}.`,
        libelle: 'Motif',
        placeholder: 'Ex. la clinique a changé d\'avis',
        libelleValider: 'Annuler le changement',
        danger: false,
        choix: {
          libelle: 'Période déjà payée au tarif réduit',
          valeur: '1',
          options: [
            { valeur: '1', texte: "Facturer la différence au prorata (recommandé)" },
            { valeur: '0', texte: 'Ne rien facturer (geste commercial)' }
          ]
        }
      })
      if (!r) return
      try {
        const res = (await api.post(`plateforme/cliniques/${c.id}/changement-prevu/annuler`, { motif: r.motif, facturerDifference: r.choix === '1' })).data
        notifier(res.message, 'success')
        await this.charger()
      } catch (e) {
        notifier(messageErreur(e), 'danger')
      }
    },
    dateCourte(d) {
      return d ? dayjs(d).format('DD/MM/YYYY') : ''
    },
    infoDonneesEssai(c) {
      const d = c.donneesEssai
      if (!d) return ''
      if (d.choix) return `${d.choix === 'GARDEES' ? 'Données de l\'essai conservées' : 'Données de l\'essai effacées (sauvegarde gardée)'} le ${dayjs(d.choisiLe).format('DD/MM/YYYY HH:mm')}${d.choisiPar ? ' par ' + d.choisiPar : ''}`
      if (d.aChoisir) return `Passée en payant le ${dayjs(d.passageActifLe).format('DD/MM/YYYY')} : la clinique a jusqu'au ${dayjs(d.dateLimite).format('DD/MM/YYYY')} pour choisir (sans choix, tout est conservé).`
      return `Pas de choix fait avant le ${dayjs(d.dateLimite).format('DD/MM/YYYY')} : données de l'essai conservées.`
    },
    badgeStatut(c) {
      if (c.statut === 'SUPPRIMEE') return 'bg-dark'
      if (c.statut === 'SUSPENDU' || (c.joursRestants !== null && c.joursRestants < -7)) return 'bg-danger'
      if (c.joursRestants !== null && c.joursRestants <= 7) return 'bg-warning text-dark'
      return c.statut === 'ESSAI' ? 'bg-info text-dark' : 'bg-success'
    },
    formatDate(d) {
      return dayjs(d).format('DD/MM/YYYY')
    }
  }
}
</script>

<style scoped>
.header-clinique { padding: 15px 20px; background-color: #e9f5ff; border-radius: 12px; }
.card-clinique { background: #f8fafd; border-radius: 12px; border: none; }
.btn-clinique { background: linear-gradient(90deg, #0d6efd, #6610f2); border: none; color: #fff; border-radius: 10px; }
.modern-table-clinique thead { background: linear-gradient(90deg, #8fd3f4, #0d6efd); color: #fff; }
.modal-backdrop-custom { position: fixed; inset: 0; background: rgba(0, 0, 0, .45); z-index: 1050; display: flex; align-items: flex-start; justify-content: center; padding: 40px 16px; overflow-y: auto; }
.modal-card { width: 100%; max-width: 760px; border-radius: 12px; }
.point-activite { display: inline-block; width: 9px; height: 9px; border-radius: 50%; flex-shrink: 0; }
.point-activite.vert { background: #198754; }
.point-activite.orange { background: #fd7e14; }
.point-activite.rouge { background: #dc3545; }
.point-activite.gris { background: #adb5bd; }
</style>
